// v2 snow-fir — a winter fir: the v2 fir's dense to-the-ground construction in
// darker winter greens, carrying wind-blown snow: every tier wears a white cap
// nudged toward the same leeward side, a snowdrift mounds against the foot, and
// the tip is snowed over white. Variants turn the wind, re-roll the tier radii
// and shift the winter palette deterministically. Ported 1:1 from age-of-mcp's
// v2/tree-snow-fir.ts, using the library's shared `tree` (h01, lerp, lobe, pick,
// tier) helpers.
import * as THREE from "three";
import { defineModel, tree, type Part } from "../../lib";

const PALETTES = [
  { shadow: "#1d4434", deep: "#23523d", mid: "#2a5f46" },
  { shadow: "#1b4236", deep: "#21503f", mid: "#285d48" },
  { shadow: "#1f4632", deep: "#25543b", mid: "#2c6144" },
] as const;

const SNOW = "#e9f2f8";
const SNOW_BRIGHT = "#f7fbfe";
const BARK = "#39291c";

// Foliage tiers [y, r, h] and the snow cap [y, r, h] draped on each.
const TIERS: readonly [number, number, number][] = [
  [0.38, 0.56, 0.46],
  [0.84, 0.42, 0.44],
  [1.22, 0.3, 0.42],
  [1.54, 0.2, 0.38],
];
const CAPS: readonly [number, number, number][] = [
  [0.64, 0.4, 0.22],
  [1.06, 0.31, 0.2],
  [1.42, 0.22, 0.18],
];

function snowFirParts(v: number): Part[] {
  const seed = 197 + v * 29;
  const pal = tree.pick(PALETTES, v);
  // one leeward wind direction per variant — all caps drift the same way
  const wind = v * 2.3 + 0.7;
  const wx = Math.cos(wind), wz = Math.sin(wind);
  const parts: Part[] = [
    // trunk foot + the snowdrift mounded against it
    { geometry: new THREE.CylinderGeometry(0.07, 0.1, 0.12, 5),
      color: BARK, position: [0, 0.06, 0] },
    tree.lobe(seed, 2, wx * 0.12, 0.05, wz * 0.12, 0.3, SNOW, 0.25),
  ];
  const greens = [pal.shadow, pal.deep, pal.mid, pal.mid] as const;
  for (let i = 0; i < TIERS.length; i++) {
    const [y, r, h] = TIERS[i]!;
    const jitter = tree.lerp(0.95, 1.05, tree.h01(seed, 5 + i));
    parts.push(tree.tier(seed, 10 + i * 3, y, r * jitter, h, greens[i]!, 7, 0.015));
  }
  // wind-blown snow caps draped over each tier's shoulder
  for (let i = 0; i < CAPS.length; i++) {
    const [y, r, h] = CAPS[i]!;
    parts.push({
      geometry: new THREE.ConeGeometry(r, h, 7),
      color: SNOW,
      position: [wx * 0.06, y, wz * 0.06],
      rotation: [0, tree.h01(seed, 30 + i) * Math.PI, 0],
    });
  }
  // snowed-over tip
  parts.push(tree.tier(seed, 40, 1.7, 0.1, 0.26, SNOW_BRIGHT, 6, 0.005));
  return parts;
}

export const treeSnowFirV2 = defineModel({
  id: "tree-snow-fir-v2",
  batchId: "fable",
  name: "Tree snow fir (v2)",
  tags: ["scenery","tree","snow","fir","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tree snow fir.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.5,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => snowFirParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => snowFirParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => snowFirParts(2) },
  ],
  animations: [],
});
